import { Card, InputNumber, Radio, Select, Table } from "antd";
import { get } from "lodash";

const FALLBACK_AGE_BRACKETS = [
  "0-17", "18-25", "26-35", "36-45", "46-55", "56-65", "66+",
];

const PRICING_TYPES = {
  FLAT: "flat",
  AGE: "age",
  AGE_ZONE: "age_zone",
  AGE_ZONE_REGIME: "age_zone_regime",
};

const PRICING_UNITS = [
  { value: "eur", label: "€/mois" },
  { value: "pmss", label: "% PMSS" },
];

const PricingInput = ({ value, onChange, label, zones, ageBrackets, regimes }) => {
  const hasZones = zones && zones.length > 0;
  const hasRegimes = regimes && regimes.length > 0;
  const brackets = ageBrackets && ageBrackets.length > 0
    ? ageBrackets.map((b) => b.name)
    : FALLBACK_AGE_BRACKETS;
  const currentType = get(value, "type", PRICING_TYPES.FLAT);
  const currentUnit = get(value, "unit", "eur");
  const unitSuffix = currentUnit === "pmss" ? "% PMSS" : "€/mois";

  const updateValue = (path, val) => {
    const parts = path.split(".");
    const updated = { ...value };
    let current = updated;
    for (let i = 0; i < parts.length - 1; i++) {
      current[parts[i]] = { ...get(current, parts[i], {}) };
      current = current[parts[i]];
    }
    current[parts[parts.length - 1]] = val;
    onChange(updated);
  };

  const handleTypeChange = (type) => {
    onChange({ ...value, type });
  };

  const handleUnitChange = (unit) => {
    onChange({ ...value, unit });
  };

  const ageDataSource = brackets.map((age) => ({
    key: age,
    age,
    price: get(value, `base.${age}`, ""),
  }));

  return (
    <div>
      {label && (
        <label style={{ fontSize: 12, color: "#64748B", display: "block", marginBottom: 8 }}>
          {label}
        </label>
      )}

      <Radio.Group
        value={currentType}
        onChange={(e) => handleTypeChange(e.target.value)}
        style={{ marginBottom: 12 }}
      >
        <Radio.Button value={PRICING_TYPES.FLAT}>Prix fixe</Radio.Button>
        <Radio.Button value={PRICING_TYPES.AGE}>Par âge</Radio.Button>
        {hasZones && (
          <Radio.Button value={PRICING_TYPES.AGE_ZONE}>Âge × Zone</Radio.Button>
        )}
        {hasZones && hasRegimes && (
          <Radio.Button value={PRICING_TYPES.AGE_ZONE_REGIME}>Âge × Zone × Régime</Radio.Button>
        )}
      </Radio.Group>

      <div style={{ marginBottom: 12 }}>
        <label style={{ fontSize: 12, color: "#64748B", marginRight: 8 }}>Unité :</label>
        <Select
          size="small"
          value={currentUnit}
          onChange={handleUnitChange}
          style={{ width: 120 }}
        >
          {PRICING_UNITS.map((u) => (
            <Select.Option key={u.value} value={u.value}>
              {u.label}
            </Select.Option>
          ))}
        </Select>
      </div>

      {currentType === PRICING_TYPES.FLAT && (
        <InputNumber
          min={0}
          step={0.01}
          value={get(value, "monthly", "")}
          onChange={(price) => onChange({ ...value, type: PRICING_TYPES.FLAT, monthly: price })}
          style={{ width: 200 }}
          placeholder="0.00"
          addonAfter={unitSuffix}
        />
      )}

      {currentType === PRICING_TYPES.AGE && (
        <div className="pricing-grid">
          <Table
            size="small"
            pagination={false}
            dataSource={ageDataSource}
            columns={[
              {
                title: "Âge",
                dataIndex: "age",
                width: 100,
                render: (age) => <strong>{age}</strong>,
              },
              {
                title: `Prix de base (${unitSuffix})`,
                dataIndex: "price",
                render: (val, record) => (
                  <InputNumber
                    size="small"
                    min={0}
                    step={0.01}
                    value={val}
                    onChange={(price) => updateValue(`base.${record.age}`, price)}
                    style={{ width: "100%" }}
                    placeholder="0.00"
                  />
                ),
              },
            ]}
          />
        </div>
      )}

      {currentType === PRICING_TYPES.AGE_ZONE && hasZones && (
        <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
          <div className="pricing-grid">
            <p style={{ fontSize: 12, color: "#64748B", padding: "8px 12px 0", margin: 0 }}>
              Prix de base par tranche d'âge
            </p>
            <Table
              size="small"
              pagination={false}
              dataSource={ageDataSource}
              columns={[
                {
                  title: "Âge",
                  dataIndex: "age",
                  width: 100,
                  render: (age) => <strong>{age}</strong>,
                },
                {
                  title: `Prix de base (${unitSuffix})`,
                  dataIndex: "price",
                  render: (val, record) => (
                    <InputNumber
                      size="small"
                      min={0}
                      step={0.01}
                      value={val}
                      onChange={(price) => updateValue(`base.${record.age}`, price)}
                      style={{ width: "100%" }}
                      placeholder="0.00"
                    />
                  ),
                },
              ]}
            />
          </div>
          <Card size="small" style={{ background: "#F0F9FF" }}>
            <p style={{ fontSize: 12, color: "#64748B", margin: "0 0 8px" }}>
              Modificateurs par zone (% du prix de base)
            </p>
            <div style={{ display: "flex", gap: "16px", flexWrap: "wrap" }}>
              {zones.map((zone) => (
                <div key={zone.id} style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                  <label style={{ fontSize: 13, fontWeight: 500, color: "#0C4A6E" }}>
                    {zone.name} :
                  </label>
                  <InputNumber
                    size="small"
                    value={get(value, `zoneModifiers.${zone.id}`, 0)}
                    onChange={(val) => updateValue(`zoneModifiers.${zone.id}`, val)}
                    style={{ width: 90 }}
                    addonAfter="%"
                    placeholder="0"
                  />
                </div>
              ))}
            </div>
          </Card>
        </div>
      )}

      {currentType === PRICING_TYPES.AGE_ZONE_REGIME && hasZones && hasRegimes && (
        <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
          <div className="pricing-grid">
            <p style={{ fontSize: 12, color: "#64748B", padding: "8px 12px 0", margin: 0 }}>
              Prix de base par tranche d'âge
            </p>
            <Table
              size="small"
              pagination={false}
              dataSource={ageDataSource}
              columns={[
                {
                  title: "Âge",
                  dataIndex: "age",
                  width: 100,
                  render: (age) => <strong>{age}</strong>,
                },
                {
                  title: `Prix de base (${unitSuffix})`,
                  dataIndex: "price",
                  render: (val, record) => (
                    <InputNumber
                      size="small"
                      min={0}
                      step={0.01}
                      value={val}
                      onChange={(price) => updateValue(`base.${record.age}`, price)}
                      style={{ width: "100%" }}
                      placeholder="0.00"
                    />
                  ),
                },
              ]}
            />
          </div>
          <Card size="small" style={{ background: "#F0F9FF" }}>
            <p style={{ fontSize: 12, color: "#64748B", margin: "0 0 8px" }}>
              Modificateurs par zone (% du prix de base)
            </p>
            <div style={{ display: "flex", gap: "16px", flexWrap: "wrap" }}>
              {zones.map((zone) => (
                <div key={zone.id} style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                  <label style={{ fontSize: 13, fontWeight: 500, color: "#0C4A6E" }}>
                    {zone.name} :
                  </label>
                  <InputNumber
                    size="small"
                    value={get(value, `zoneModifiers.${zone.id}`, 0)}
                    onChange={(val) => updateValue(`zoneModifiers.${zone.id}`, val)}
                    style={{ width: 90 }}
                    addonAfter="%"
                    placeholder="0"
                  />
                </div>
              ))}
            </div>
          </Card>
          <Card size="small" style={{ background: "#F0FFF4" }}>
            <p style={{ fontSize: 12, color: "#64748B", margin: "0 0 8px" }}>
              Modificateurs par régime (% du prix de base)
            </p>
            <div style={{ display: "flex", gap: "16px", flexWrap: "wrap" }}>
              {regimes.map((regime) => (
                <div key={regime.id} style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                  <label style={{ fontSize: 13, fontWeight: 500, color: "#0C4A6E" }}>
                    {regime.name} :
                  </label>
                  <InputNumber
                    size="small"
                    value={get(value, `regimeModifiers.${regime.id}`, 0)}
                    onChange={(val) => updateValue(`regimeModifiers.${regime.id}`, val)}
                    style={{ width: 90 }}
                    addonAfter="%"
                    placeholder="0"
                  />
                </div>
              ))}
            </div>
          </Card>
        </div>
      )}
    </div>
  );
};

export default PricingInput;
